import React, { useEffect, useRef, useState } from 'react';
import { Collapse, Stack } from '@mui/material';
import * as S from './Sidebar.styles';
import { NavListProps } from './Sidebar.types';

import { useActiveLink } from '@hook/useNavigation/useNavigation';
import SidebarItem from './SidebarItem';

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
  isMini: boolean;
};

export const SidebarList = ({
  data,
  depth,
  hasChild,
  isMini,
}: NavListRootProps) => {
  const { active } = useActiveLink(data.path, data?.subPath);
  const [open, setOpen] = useState<boolean>(active);

  useEffect(() => {
    if (isMini) setOpen(false);
  }, []);

  const handleToggle = () => {
    setOpen(!open);
  };

  const navRef = useRef(null);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (open && isMini) {
      handleClose();
    }
  }, [isMini]);

  return (
    <>
      <Stack mb={0.5}>
        <SidebarItem
          ref={navRef}
          data={data}
          depth={depth}
          open={open}
          active={active}
          isMini={isMini}
          onClick={hasChild ? handleToggle : undefined}
          onMouseEnter={isMini ? handleOpen : undefined}
          onMouseLeave={isMini ? handleClose : undefined}
        />
      </Stack>

      {hasChild && !isMini && (
        <Collapse in={open} unmountOnExit style={{ minHeight: undefined }}>
          <SideSubList data={data.children} depth={depth} isMini={isMini} />
        </Collapse>
      )}
      {hasChild && isMini && (
        <S.StyledPopover
          open={open}
          anchorEl={navRef.current}
          anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
          transformOrigin={{ vertical: 'center', horizontal: 'left' }}
          onMouseEnter={handleOpen}
          onMouseLeave={handleClose}
        >
          <SideSubList data={data.children} depth={depth} isMini={isMini} />
        </S.StyledPopover>
      )}
    </>
  );
};

type SideSubListProps = {
  data: NavListProps[];
  depth: number;
  isMini: boolean;
};

function SideSubList({ data, depth, isMini }: SideSubListProps) {
  return (
    <>
      {data.map((list) => (
        <SidebarList
          key={list.title + list.path}
          data={list}
          depth={depth + 1}
          hasChild={!!list.children}
          isMini={isMini}
        />
      ))}
    </>
  );
}

export default SidebarList;
